import ExtensionCapability from '../../capability';
import { type Message, Specs, attachHandler } from '@doxyme/extensions-common';
import type ExtensionInstance from '../../instance';

const { NavigationInterception } = Specs;
type Constraints = Specs.NavigationInterception.Constraints;
type NavigationAddPayload = Specs.NavigationInterception.NavigationAddPayload;
type NavigationRemovePayload =
  Specs.NavigationInterception.NavigationRemovePayload;

export type OnNavigationInterceptionAddHandler = (
  payload: NavigationAddPayload,
  sendMessage: (message: Message) => void,
) => void;

type OnNavigationInterceptionRemoveHandler = (
  payload: NavigationRemovePayload,
  sendMessage: (message: Message) => void,
) => void;

export default class NavigationInterceptionCapability extends ExtensionCapability<Constraints> {
  static readonly id = NavigationInterception.id;

  protected readonly blockHandlers: OnNavigationInterceptionAddHandler[] = [];
  protected readonly allowHandlers: OnNavigationInterceptionRemoveHandler[] =
    [];

  constructor() {
    super(NavigationInterception);

    this.handlers.push(
      [
        NavigationInterception.Actions.Add,
        this.addNavigationInterception.bind(this),
      ],
      [
        NavigationInterception.Actions.Remove,
        this.removeNavigationInterception.bind(this),
      ],
    );
  }

  // Method to block navigation
  private addNavigationInterception(
    payload: NavigationAddPayload,
    instance: ExtensionInstance,
  ) {
    for (const handler of this.blockHandlers) {
      handler(payload, (message: Message) =>
        this.handleMessages(instance, [message]),
      );
    }
  }

  private removeNavigationInterception(
    payload: NavigationRemovePayload,
    instance: ExtensionInstance,
  ) {
    for (const handler of this.allowHandlers) {
      handler(payload, (message: Message) =>
        this.handleMessages(instance, [message]),
      );
    }
  }

  onAdd(handler: OnNavigationInterceptionAddHandler): () => void {
    return attachHandler<OnNavigationInterceptionAddHandler>(
      this.blockHandlers,
      handler,
    );
  }

  onRemove(handler: OnNavigationInterceptionRemoveHandler): () => void {
    return attachHandler<OnNavigationInterceptionRemoveHandler>(
      this.allowHandlers,
      handler,
    );
  }
}
